/**
 * flickerConfig — the pure math for the `flicker` scene-effect: anchored glows
 * whose alpha shimmers (torch / heat-haze / caustic light). No Pixi here; the
 * flicker effect builder (atmosphere/effects/flickerEffect.ts) consumes these
 * helpers to animate each anchor's glow sprite.
 *
 * Split out (the same discipline as atmospherePlacement.ts / softLightConfig.ts)
 * so the shimmer curve + anchor placement are unit-testable without a renderer.
 */
import {
  resolveEffectAnchor,
  seededRand,
  type Region,
} from '../atmosphere/atmospherePlacement';
import type { EffectAnchor } from '../../../config/atmosphere/presets.config';
import { lerp, clamp01 } from '../../../shared/utils/MathUtils';

/**
 * Smooth pseudo-random shimmer alpha for a flicker glow.
 *
 * Sums two sines — one at `hz`, one at ~2.3×hz — each with a seed-derived phase,
 * normalised to [-1, 1], scaled by `flickerAmp`, and centred on `baseAlpha`. The
 * incommensurate frequencies make the result read as an organic flicker (never a
 * clean repeating pulse). Result is clamped to a valid alpha [0, 1].
 *
 * @param elapsedMs accumulated time for this glow (ms)
 * @param hz        primary flicker rate (cycles/sec)
 * @param baseAlpha mean alpha the glow sits at
 * @param flickerAmp alpha swing added/subtracted around baseAlpha
 * @param seed      per-anchor seed → stable phase offset (deterministic)
 */
export { seededRand } from '../atmosphere/atmospherePlacement';

export function flickerAlpha(
  elapsedMs: number,
  hz: number,
  baseAlpha: number,
  flickerAmp: number,
  seed: number,
): number {
  const t = elapsedMs / 1000;
  const phase1 = seededRand(seed * 1.61 + 7) * Math.PI * 2;
  const phase2 = seededRand(seed * 2.37 + 19) * Math.PI * 2;
  const w = 2 * Math.PI * hz;
  // Two incommensurate sines, summed and normalised back into [-1, 1].
  const shimmer = (Math.sin(w * t + phase1) + Math.sin(w * 2.3 * t + phase2)) / 2;
  return clamp01(baseAlpha + shimmer * flickerAmp);
}

/** A flicker anchor resolved to a world position + its own seeded flicker rate. */
export interface PlacedFlicker {
  x: number;
  y: number;
  /** Per-anchor flicker rate (Hz), drawn from [hzMin, hzMax] by the seed. */
  hz: number;
  /** Per-anchor seed → stable phase in flickerAlpha + stable hz. */
  seed: number;
}

/**
 * Resolve each normalized-region or absolute-world anchor to a world position
 * and assign it a seeded flicker rate in [hzMin, hzMax]. Deterministic for a
 * given `baseSeed` so a level's torches sit + shimmer the same across reloads.
 */
export function placeFlickers(
  anchors: readonly EffectAnchor[],
  region: Region,
  hzMin: number,
  hzMax: number,
  baseSeed: number,
): PlacedFlicker[] {
  return anchors.map((a, i) => {
    const seed = baseSeed + i * 5.77;
    const position = resolveEffectAnchor(a, region);
    return {
      ...position,
      hz: lerp(hzMin, hzMax, seededRand(seed + 3)),
      seed,
    };
  });
}

/**
 * Re-base placed flicker glows onto a surface world-Y (e.g. a `'lava'` scene
 * region top) so volcanic heat-haze shimmers along the lava line rather than
 * scattered across the camera band. Pass `surfaceY = null` (no such region) to
 * leave the placements untouched. Pure — keeps the X + per-anchor seed/hz. (T7)
 */
export function anchorFlickersToSurface(
  placed: PlacedFlicker[],
  surfaceY: number | null,
): PlacedFlicker[] {
  if (surfaceY === null) return placed;
  return placed.map((p) => ({ ...p, y: surfaceY }));
}
